import { Popover as P } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  /** The panel's accessible name (it is a dialog). */
  label?: string;
  /** Move focus into the panel when it opens (default). False keeps it on the trigger, for a
   * read-only note. */
  focusContent?: boolean;
  /** Escape inside the panel; `preventDefault()` keeps it open (an open list inside closes first). */
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
}

/** Opaque floating panel under its trigger, portaled so no parent clips it. */
export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  align = "start",
  side = "bottom",
  className,
  label,
  focusContent = true,
  onEscapeKeyDown,
}: PopoverProps) {
  return (
    <P.Root open={open} onOpenChange={onOpenChange}>
      <P.Trigger asChild>{trigger}</P.Trigger>
      <P.Portal>
        <P.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          aria-label={label}
          {...(focusContent ? {} : { onOpenAutoFocus: (e: Event) => e.preventDefault() })}
          {...(onEscapeKeyDown ? { onEscapeKeyDown } : {})}
          className={cn(
            "z-50 rounded-lg bg-overlay p-1 text-ink shadow-popover outline-none",
            "data-[state=closed]:animate-vd-fade-out data-[state=open]:animate-vd-pop-in",
            className,
          )}
        >
          {children}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}

export const PopoverClose = P.Close;
